import * as Phaser from "phaser-ce"
import { CCUtils } from "../../common/CCUtils"
import { log } from "../../common/Logging"
import { Align, FontType } from "../../common/TextUtils"
import { colorNum } from "../utils/ColorUtils"
import { PhaserText } from "../utils/PhaserText"
import { Scale } from "../utils/Scale"
import { Sprites } from "../utils/Sprites"
import { Buttons } from "./Buttons"
import { GenericAssets } from "./GenericAssets"

/**
 * Sprite that expands itself to represent that it is clickable when it is moused over. It can begin "off" and get
 * turned on later. Also, if you add an onClick event to this, that will only be executed if the sprite is on.
 */
export class ExpandoSprite extends Phaser.Sprite {

    static readonly SCALE_AMOUNT = 1.1
    private static readonly GRAY_OUT_TINT = colorNum.gray

    on: boolean
    title: Phaser.Group
    private defaultScale: number
    private widthOffset: number
    private heightOffset: number
    private currentlyExpanded = false
    private firstTime = true
    private clickHere: Phaser.Text
    private readonly ignoreCoors: Phaser.Sprite
    private readonly onExpand: () => void

    static expandoFactory = (game: Phaser.Game, scale = 1, on = true, group?: Phaser.Group) => {
        return (x: number, y: number, key: string) => new ExpandoSprite(game, x, y, key, {scale, on, group})
    }

    constructor(
        game: Phaser.Game, x: number, y: number, key: string,
        {scale = 1, on = true, debug = false,  group, ignoreCoors, onExpand}:
            { scale?: number, on?: boolean, debug?: boolean, group?: Phaser.Group, ignoreCoors?: Phaser.Sprite, onExpand?: () => void } = {}
    ) {
        super(game, x, y, key)

        // log.debug("Creating expando sprite " + key + ". scale: " + scale + " on: " + on + " group: " + group)
        this.ignoreCoors = ignoreCoors

        game.physics.enable(this, Phaser.Physics.ARCADE)
        this.changeScale(scale)
        if (group) {
            group.add(this)
        } else {
            this.game.add.existing(this)
        }
        this.on = on

        if (debug) {
            Sprites.enableDebug(this)
        }

        if (onExpand) {
            this.onExpand = onExpand
        }
    }

    acceptInput = () => (
        Phaser.Rectangle.contains(this.body, this.game.input.x, this.game.input.y) &&
        (this.ignoreCoors == null || !Phaser.Rectangle.contains(this.ignoreCoors.body, this.game.input.x, this.game.input.y))
    )

    update() {
        if (!this.on) {
            return
        } else if (this.firstTime) {
            Buttons.makeSpriteButton(this)
            this.firstTime = false
        }

        if (this.acceptInput()) {
            if (!this.currentlyExpanded) {
                if (this.onExpand) {
                    this.onExpand()
                }
                Scale.scaleSprite(this, this.defaultScale * 1.1)
                this.x = this.expandoOffsetX()
                this.y = this.expandoOffsetY()
                this.currentlyExpanded = true
                if (this.clickHere) {
                    this.clickHere.visible = false
                }
                if (this.title) {
                    this.title.visible = true
                }
            }
        } else {
            if (this.currentlyExpanded) {
                this.unexpand()
            }
        }
        if (this.title) {
            this.title.alpha = this.alpha
            if (!this.visible) {
                this.title.visible = false
            }
        }
    }

    addOnClick = (onClick: (clicked: ExpandoSprite) => void) => {
        const signalBinding = this.game.input.onDown.add((clicked) => {
            if (!this.game) {
                signalBinding.detach()
            } else {
                if (this.on && this.acceptInput()) {
                    log.debug("Expando sprite got click.")
                    onClick(clicked)
                }
            }
        })
    }

    addClickHereText = (x: number, y: number, oneLine = true) => {
        this.clickHere = PhaserText.create(this.game, "Click" + (oneLine ? " " : "\n") + "here", {
            x, y, fontPx: 20, type: FontType.BODY
        })
    }

    addTitle = (
        titleText: string,
        {x, y, fontType = FontType.TITLE, firstCharCaps = false}:
            { x?: number, y?: number, linesHigh?: number, fontType?: FontType, firstCharCaps?: boolean } = {}
    ) => {
        if (x == null) {
            x = this.x + this.width
        }
        if (y == null) {
            y = this.y
        }
        if (firstCharCaps) {
            titleText = CCUtils.capsFirstChar(titleText)
        }
        this.title = this.game.add.group()
        const textWidthPadding = 20 * Scale.scaleAmount()
        const textHeightPadding = 8 * Scale.scaleAmount()

        const box = this.title.create(x, y, GenericAssets.TEXT_BOX)
        const textX = x + textWidthPadding
        const textY = y + textHeightPadding + Scale.scaleAmount() * 3
        const titleSprite = PhaserText.create(this.game, titleText, {
            x: textX, y: textY, type: fontType, fontPx: 20, align: Align.CENTER, group: this.title
        })

        box.width = titleSprite.width + textWidthPadding * 2
        box.height = titleSprite.height + textHeightPadding * 2
        this.title.visible = false
    }

    expandoOffsetX = (): number => {
        return this.x - this.widthOffset
    }

    expandoOffsetY = (): number => {
        return this.y - this.heightOffset
    }

    grayOut = (gray = true) => {
        log.debug("Graying out expando sprite: " + this.key)
        if (gray) {
            this.tint = ExpandoSprite.GRAY_OUT_TINT
        } else {
            this.tint = colorNum.white
        }
    }

    isGrayedOut = (): boolean => {
        return this.tint === ExpandoSprite.GRAY_OUT_TINT
    }

    changeScale = (scale: number) => {
        this.defaultScale = scale
        Scale.scaleSprite(this, scale)

        this.widthOffset = (this.width * ExpandoSprite.SCALE_AMOUNT - this.width) / 2
        this.heightOffset = (this.height * ExpandoSprite.SCALE_AMOUNT - this.height) / 2
    }

    unexpand = () => {
        Scale.scaleSprite(this, this.defaultScale)
        this.x = this.x + this.widthOffset
        this.y = this.y + this.heightOffset
        this.currentlyExpanded = false
        if (this.clickHere) {
            this.clickHere.visible = true
        }
        if (this.title) {
            this.title.visible = false
        }
    }

    turnOff = () => {
        this.on = false
        if (this.title) {
            this.title.visible = false
        }
        Buttons.undoSpriteButton(this)
    }

    turnOn = () => {
        this.on = true
        Buttons.makeSpriteButton(this)
    }
}
